import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Save,
  X,
  Plus,
  Trash2,
  Edit3,
  RefreshCw,
  AlertCircle,
  ChefHat,
  ChevronDown,
  ChevronRight,
  GlassWater,
} from 'lucide-react';
import apiClient from '../services/api';
import ConfirmModal from '../components/modals/ConfirmModal';
import { tipoPlatoUsaFondoGuarnicion } from '../utils/minutaDisplay';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface Receta {
  id: number;
  nombre: string;
  descripcion: string | null;
  tiempo_preparacion: number;
  costo_total: string;
}

// Modelo de opciones por minuta (ver
// db/migrations/2026-04-26_modelo_opciones_minuta_con_rol.sql).
//   fila_orden = número de OPCIÓN (1, 2, ...). El "+" la incrementa.
//   rol        = slot dentro de la opción:
//                'unico' (tipos simples) | 'fondo' / 'guarnicion'
//                (tipos combinados id 3 = COLACIÓN PRINCIPAL, id 10 = CENA).
type RolMinutaDetalle = 'unico' | 'fondo' | 'guarnicion';

interface TipoPlato {
  id: number;
  nombre: string;
  color: string;
  orden: number;
  fila_orden?: number;
  rol?: RolMinutaDetalle;
  omitido?: boolean;
}

const ESTADOS_BLOQUEAN_EDICION: ReadonlyArray<string> = [
  'completada',
  'cancelada',
];

interface DiaMinuta {
  fecha: string;
  nombre: string;
  receta: Receta | null;
  minutaDetalladaId: number | null;
  // Marca celda como "no aplica" — descontada del % de completitud.
  celdaOmitida?: boolean;
}

interface FilaTipoPlato {
  tipoPlato: TipoPlato;
  dias: DiaMinuta[];
}

interface DatosEditorMinuta {
  minuta: {
    id: number;
    nombre: string;
    estado?: string;
  };
  configuracion: {
    totalDias: number;
    totalTiposPlato: number;
    totalCeldas: number;
    recetasAsignadas: number;
  };
  dias: Array<{
    fecha: string;
    nombre: string;
  }>;
  matriz: FilaTipoPlato[];
  tiposPlato?: TipoPlato[];
}

interface RecetaDisponible {
  id: number;
  nombre: string;
  descripcion: string | null;
  tiempo_preparacion: number;
  costo_total: string;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const EditorMinuta = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Estados principales
  const [datos, setDatos] = useState<DatosEditorMinuta | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados para edición de fechas
  const [editandoFechas, setEditandoFechas] = useState(false);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [_nombreDinamico, _setNombreDinamico] = useState('');

  // Estados para edición de título
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [nuevoTitulo, setNuevoTitulo] = useState('');

  // Estados para control de guardado
  const [cambiosPendientes, setCambiosPendientes] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cambiosLocales, setCambiosLocales] = useState<
    Map<string, { recetaId: number | null; tipoPlato: TipoPlato }>
  >(new Map());

  // Estados para toast
  const [mostrarToast, setMostrarToast] = useState(false);
  const [toastMensaje, setToastMensaje] = useState('');
  const [toastTipo, setToastTipo] = useState<'success' | 'error'>('success');

  // Estados para modal de confirmación al cerrar
  const [mostrarModalCerrar, setMostrarModalCerrar] = useState(false);

  // 2026-06-16 — UI: la sección de bebidas se renderiza colapsada por
  // default. Toggle del usuario sobre la barra "Bebidas (N incluidas)".
  // Sin cambios de lógica: auto-seed, guardado y validación intactos.
  const [bebidasExpandidas, setBebidasExpandidas] = useState(false);

  // Estados para selector de recetas inline
  const [selectorActivo, setSelectorActivo] = useState<string>('');
  const [busquedaReceta, setBusquedaReceta] = useState('');
  const [recetasDisponibles, setRecetasDisponibles] = useState<
    RecetaDisponible[]
  >([]);
  const [cargandoRecetas, setCargandoRecetas] = useState(false);
  // Tipo de plato + rol activo en el selector. El rol determina qué
  // catálogo se filtra cuando el tipo es combinado:
  //   rol='fondo'      → tipo_plato = 'FONDO'.
  //   rol='guarnicion' → tipo_plato = 'GUARNICIÓN'.
  //   rol='unico'      → tipo_plato = nombre base del tipo.
  const [tipoSelectorActivo, setTipoSelectorActivo] = useState<{
    id: number;
    nombre: string;
    rol: RolMinutaDetalle;
  } | null>(null);

  // Estados para modal de confirmación
  const [mostrarModalConfirm, setMostrarModalConfirm] = useState(false);
  const [modalConfirmConfig, setModalConfirmConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'warning' | 'danger' | 'info';
    confirmText?: string;
    cancelText?: string;
  }>({
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
  });

  // ============================================================================
  // ESTADÍSTICAS EN VIVO (derivadas de datos.matriz)
  // ============================================================================
  // 2026-06-16 — bug: los contadores "Recetas Asignadas" y "Total Celdas"
  // del sidebar leían `datos.configuracion.*` (snapshot del backend al
  // cargar), por lo que NO se actualizaban al editar (agregar/quitar
  // receta, marcar "No aplica", borrar fila). El estado `datos.matriz`
  // SÍ se actualiza en cada handler (removerReceta, asignarReceta,
  // toggleCeldaOmitida, etc.), así que derivamos los contadores de ahí.
  //
  // Definición final de completitud (sesión #026):
  //   - COMPLETA = receta asignada O "No aplica".
  //   - PENDIENTE = sin receta Y sin "No aplica".
  //   - % = completas / total. Cap 100%.
  //   - % y barra usan los mismos números.
  const estadisticasEnVivo = useMemo(() => {
    if (!datos) {
      return {
        recetasAsignadas: 0,
        celdasOmitidas: 0,
        celdasCompletas: 0,
        totalCeldas: 0,
        completitudPorcentaje: 0,
      };
    }
    // Solo cuentan filas con rol 'unico' o 'fondo' (mismo criterio
    // del backend D-022: la guarnición de un compuesto no doble-cuenta).
    let recetas = 0;
    let omitidas = 0;
    let total = 0;
    for (const fila of datos.matriz) {
      const rol = fila.tipoPlato.rol || 'unico';
      if (rol === 'guarnicion') continue;
      for (const dia of fila.dias) {
        total += 1;
        if (dia.receta) recetas += 1;
        else if (dia.celdaOmitida) omitidas += 1;
      }
    }
    const completas = recetas + omitidas;
    const crudo =
      total > 0 ? Math.round((completas / total) * 100) : 0;
    return {
      recetasAsignadas: recetas,
      celdasOmitidas: omitidas,
      celdasCompletas: completas,
      totalCeldas: total,
      completitudPorcentaje: Math.min(100, Math.max(0, crudo)),
    };
  }, [datos]);

  // ============================================================================
  // FUNCIONES DE CARGA DE DATOS
  // ============================================================================

  const cargarDatosMinuta = async () => {
    if (!id) return;

    try {
      setCargando(true);
      const response = await apiClient.get(`/minutas-nuevas/${id}/editar`);
      console.log('🔍 Respuesta del backend:', response);
      console.log('🔍 response.data:', response.data);
      console.log('🔍 response.data.data:', response.data.data);
      console.log('🔍 Matriz completa:', response.data.data.matriz);

      // Verificar recetas asignadas en la matriz
      if (response.data.data.matriz) {
        response.data.data.matriz.forEach((fila: any, index: number) => {
          console.log(`🔍 Fila ${index} - Tipo: ${fila.tipoPlato.nombre}`);
          fila.dias.forEach((dia: any) => {
            if (dia.receta) {
              console.log(
                `  ✅ ${dia.fecha}: ${dia.receta.nombre} (ID: ${dia.receta.id})`
              );
            }
          });
        });
      }

      // El backend devuelve { success: true, data: {...} }
      const datosMinuta = response.data.data;
      setDatos(datosMinuta);

      // Establecer fechas por defecto
      if (datosMinuta.dias && datosMinuta.dias.length > 0) {
        setFechaInicio(datosMinuta.dias[0].fecha);
        setFechaFin(datosMinuta.dias[datosMinuta.dias.length - 1].fecha);
      }
    } catch (error) {
      console.error('Error cargando minuta:', error);
      setError('Error al cargar los datos de la minuta');
    } finally {
      setCargando(false);
    }
  };

  const cargarRecetasDisponibles = async (
    tipoId: number,
    tipoNombre: string,
    rol: RolMinutaDetalle,
    search: string = ''
  ) => {
    try {
      setCargandoRecetas(true);
      // El dropdown muestra TODAS las recetas activas de la empresa, sin
      // filtro por tipo_plato. La búsqueda por texto sigue funcionando.
      // Ver TD-013 sobre el límite hardcoded.
      console.log(
        `🔍 Cargando recetas (sin filtro por tipo): contexto tipo="${tipoNombre}" (ID:${tipoId}) rol=${rol} search="${search}"`
      );

      const params = new URLSearchParams({
        activo: 'true',
        limit: '5000',
      });
      if (search.trim()) {
        params.set('search', search.trim());
      }
      const response = await apiClient.get(`/recetas?${params.toString()}`);

      console.log(
        `✅ Recetas encontradas para "${tipoNombre}":`,
        response.data.data?.length || 0
      );

      // El backend devuelve { data: [...], pagination: {...} }
      setRecetasDisponibles(response.data.data || []);
    } catch (error) {
      console.error('Error cargando recetas:', error);
      setRecetasDisponibles([]);
    } finally {
      setCargandoRecetas(false);
    }
  };

  // ============================================================================
  // FUNCIONES CRUD
  // ============================================================================

  const actualizarFechas = async () => {
    if (!id) return;

    try {
      // Generar el nuevo nombre basado en las fechas
      const nuevoNombre = generarNombreDinamico(fechaInicio, fechaFin);

      const response = await apiClient.put(`/minutas-nuevas/${id}/fechas`, {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        nombre: nuevoNombre,
      });

      setEditandoFechas(false);
      await cargarDatosMinuta();

      // Mostrar mensaje si se eliminaron recetas
      if (
        response.data.recetasEliminadas &&
        response.data.recetasEliminadas > 0
      ) {
        setModalConfirmConfig({
          title: 'Fechas Actualizadas',
          message: `Las fechas se actualizaron correctamente.\n\nSe eliminaron ${response.data.recetasEliminadas} receta${response.data.recetasEliminadas > 1 ? 's' : ''} que quedaron fuera del nuevo rango de fechas.`,
          type: 'info',
          confirmText: 'Entendido',
          cancelText: '',
          onConfirm: () => {
            setMostrarModalConfirm(false);
          },
        });
        setMostrarModalConfirm(true);
      }
    } catch (error) {
      console.error('Error actualizando fechas:', error);
      setError('Error al actualizar las fechas');
    }
  };

  const actualizarTitulo = async () => {
    if (!id || !nuevoTitulo.trim()) return;

    try {
      await apiClient.put(`/minutas-nuevas/${id}/titulo`, {
        nombre: nuevoTitulo.trim(),
      });

      setEditandoTitulo(false);
      await cargarDatosMinuta();
    } catch (error) {
      console.error('Error actualizando título:', error);
      setError('Error al actualizar el título');
    }
  };

  const guardarCambiosPendientes = async () => {
    if (!id || cambiosLocales.size === 0) return;

    setGuardando(true);
    try {
      // Procesar todos los cambios
      for (const [clave, cambio] of cambiosLocales.entries()) {
        // Acciones de agregar opción (legacy alias /agregar-fila sigue
        // disponible en backend; el endpoint canónico es /agregar-opcion).
        if (cambio.recetaId === -1) {
          await apiClient.post(`/minutas-nuevas/${id}/agregar-opcion`, {
            tipo_plato_nombre: cambio.tipoPlato.nombre,
          });
          continue;
        }

        // Acciones de eliminar fila
        if (cambio.recetaId === -2) {
          await apiClient.delete(`/minutas-nuevas/${id}/eliminar-fila`, {
            data: {
              tipo_plato_nombre: cambio.tipoPlato.nombre,
            },
          });
          continue;
        }

        // Acciones de toggle tipo (omitir)
        if (cambio.recetaId === -3) {
          await apiClient.put(`/minutas-nuevas/${id}/toggle-tipo-plato`, {
            tipo_plato_nombre: cambio.tipoPlato.nombre,
            omitir: true,
          });
          continue;
        }

        // Acciones de toggle tipo (reactivar)
        if (cambio.recetaId === -4) {
          await apiClient.put(`/minutas-nuevas/${id}/toggle-tipo-plato`, {
            tipo_plato_nombre: cambio.tipoPlato.nombre,
            omitir: false,
          });
          continue;
        }

        // Acciones normales de asignar/remover recetas.
        // Clave: "YYYY-MM-DD-tipoPlatoId-filaOrden-rol".
        const partesClave = clave.split('-');
        // Reconstruir la fecha (primeras 3 partes: año-mes-día)
        const fecha = `${partesClave[0]}-${partesClave[1]}-${partesClave[2]}`;
        const rolEfectivo: RolMinutaDetalle = cambio.tipoPlato.rol || 'unico';

        if (cambio.recetaId === null) {
          await apiClient.delete(`/minutas-nuevas/${id}/remover-receta`, {
            data: {
              fecha_especifica: fecha,
              tipo_plato_id: cambio.tipoPlato.id,
              fila_orden: cambio.tipoPlato.fila_orden || 1,
              rol: rolEfectivo,
            },
          });
        } else {
          await apiClient.post(`/minutas-nuevas/${id}/asignar-receta`, {
            fecha_especifica: fecha,
            tipo_plato_id: cambio.tipoPlato.id,
            receta_id: cambio.recetaId,
            fila_orden: cambio.tipoPlato.fila_orden || 1,
            rol: rolEfectivo,
          });
        }
      }

      // Limpiar cambios pendientes
      setCambiosLocales(new Map());
      setCambiosPendientes(false);

      // Recargar datos desde el servidor
      await cargarDatosMinuta();

      // Mostrar toast de éxito
      setToastMensaje('Cambios guardados exitosamente');
      setToastTipo('success');
      setMostrarToast(true);
      setTimeout(() => setMostrarToast(false), 3000);
    } catch (error) {
      console.error('Error guardando cambios:', error);
      setToastMensaje('Error al guardar los cambios');
      setToastTipo('error');
      setMostrarToast(true);
      setTimeout(() => setMostrarToast(false), 3000);
    } finally {
      setGuardando(false);
    }
  };

  // Recorre la matriz y devuelve las celdas obligatorias que están sin
  // decidir (sin receta y sin "No aplica"). Las filas con rol='guarnicion'
  // son opcionales dentro de la opción combinada y NO entran al cálculo,
  // mismo criterio que el numerador de completitud (ver D-022).
  const calcularCeldasFaltantes = (): Array<{
    fecha: string;
    fechaNombre: string;
    tipoNombre: string;
  }> => {
    if (!datos) return [];
    const faltantes: Array<{
      fecha: string;
      fechaNombre: string;
      tipoNombre: string;
    }> = [];
    for (const fila of datos.matriz) {
      const rol = fila.tipoPlato.rol || 'unico';
      if (rol === 'guarnicion') continue;
      for (const dia of fila.dias) {
        if (!dia.receta && !dia.celdaOmitida) {
          faltantes.push({
            fecha: dia.fecha,
            fechaNombre: dia.nombre,
            tipoNombre: fila.tipoPlato.nombre,
          });
        }
      }
    }
    faltantes.sort((a, b) => {
      if (a.fecha !== b.fecha) return a.fecha < b.fecha ? -1 : 1;
      return a.tipoNombre.localeCompare(b.tipoNombre);
    });
    return faltantes;
  };

  // Guardar permisivo: persiste lo que haya en cambios pendientes sin
  // bloquear ni interrumpir. La regla de negocio es clara: GUARDAR es
  // silencioso (acepta minutas parciales, sin advertencias); PUBLICAR
  // sigue siendo estricto y bloquea si faltan celdas (lógica en
  // MinutasNuevas.tsx + backend actualizarEstadoMinuta).
  // El badge "faltan N para publicar" en el header informa sin presionar.
  const guardarCambios = async (cerrarTrasGuardar = false) => {
    if (!cambiosPendientes && !cerrarTrasGuardar) return;
    await guardarCambiosPendientes();
    if (cerrarTrasGuardar) navigate('/minutas-nuevas');
  };

  const manejarCerrarEditor = () => {
    if (cambiosPendientes) {
      setMostrarModalCerrar(true);
    } else {
      navigate('/minutas-nuevas');
    }
  };

  const cerrarSinGuardar = () => {
    setCambiosLocales(new Map());
    setCambiosPendientes(false);
    navigate('/minutas-nuevas');
  };

  const guardarYCerrar = async () => {
    await guardarCambios(true);
  };

  const asignarReceta = async (
    fecha: string,
    tipoPlato: TipoPlato,
    recetaId: number
  ) => {
    if (!id) return;

    try {
      // Guardar cambio localmente. Clave incluye rol para distinguir
      // Fondo y Guarnición de la misma opción.
      const rolEfectivo: RolMinutaDetalle = tipoPlato.rol || 'unico';
      const clave = `${fecha}-${tipoPlato.id}-${tipoPlato.fila_orden || 1}-${rolEfectivo}`;
      setCambiosLocales((prev) => {
        const nuevos = new Map(prev);
        nuevos.set(clave, { recetaId, tipoPlato });
        return nuevos;
      });
      setCambiosPendientes(true);

      // Actualizar UI inmediatamente
      setDatos((prevDatos) => {
        if (!prevDatos) return prevDatos;

        const nuevaMatriz = prevDatos.matriz.map((fila) => {
          if (
            fila.tipoPlato.id === tipoPlato.id &&
            fila.tipoPlato.fila_orden === tipoPlato.fila_orden &&
            (fila.tipoPlato.rol || 'unico') === rolEfectivo
          ) {
            return {
              ...fila,
              dias: fila.dias.map((dia) => {
                if (dia.fecha === fecha) {
                  // Buscar la receta en recetasDisponibles
                  const receta = recetasDisponibles.find(
                    (r) => r.id === recetaId
                  );
                  return {
                    ...dia,
                    receta: receta
                      ? {
                          id: receta.id,
                          nombre: receta.nombre,
                          descripcion: receta.descripcion,
                          tiempo_preparacion: receta.tiempo_preparacion,
                          costo_total: receta.costo_total,
                        }
                      : null,
                  };
                }
                return dia;
              }),
            };
          }
          return fila;
        });

        return {
          ...prevDatos,
          matriz: nuevaMatriz,
        };
      });

      setSelectorActivo('');
      setBusquedaReceta('');
    } catch (error) {
      console.error('Error asignando receta:', error);
    }
  };

  const removerReceta = async (fecha: string, tipoPlato: TipoPlato) => {
    if (!id) return;

    try {
      // Guardar cambio localmente. Clave incluye rol.
      const rolEfectivo: RolMinutaDetalle = tipoPlato.rol || 'unico';
      const clave = `${fecha}-${tipoPlato.id}-${tipoPlato.fila_orden || 1}-${rolEfectivo}`;
      setCambiosLocales((prev) => {
        const nuevos = new Map(prev);
        nuevos.set(clave, { recetaId: null, tipoPlato });
        return nuevos;
      });
      setCambiosPendientes(true);

      // Actualizar UI inmediatamente
      setDatos((prevDatos) => {
        if (!prevDatos) return prevDatos;

        const nuevaMatriz = prevDatos.matriz.map((fila) => {
          if (
            fila.tipoPlato.id === tipoPlato.id &&
            fila.tipoPlato.fila_orden === tipoPlato.fila_orden &&
            (fila.tipoPlato.rol || 'unico') === rolEfectivo
          ) {
            return {
              ...fila,
              dias: fila.dias.map((dia) => {
                if (dia.fecha === fecha) {
                  return {
                    ...dia,
                    receta: null,
                    minutaDetalladaId: null,
                  };
                }
                return dia;
              }),
            };
          }
          return fila;
        });

        return {
          ...prevDatos,
          matriz: nuevaMatriz,
        };
      });
    } catch (error) {
      console.error('Error removiendo receta:', error);
    }
  };

  // Marca/desmarca una celda como "no aplica" (intencionalmente vacía).
  // Se persiste en el backend de inmediato — no es un cambio diferido como
  // las recetas, porque es operación rápida y simple.
  const toggleCeldaOmitida = async (
    fecha: string,
    tipoPlato: TipoPlato,
    omitida: boolean
  ) => {
    if (!id) return;
    try {
      const rolEfectivo: RolMinutaDetalle = tipoPlato.rol || 'unico';
      await apiClient.put(`/minutas-nuevas/${id}/toggle-celda`, {
        tipo_plato_id: tipoPlato.id,
        fecha_especifica: fecha,
        fila_orden: tipoPlato.fila_orden || 1,
        rol: rolEfectivo,
        omitida,
      });

      // Actualización optimista del state local.
      setDatos((prevDatos) => {
        if (!prevDatos) return prevDatos;
        const nuevaMatriz = prevDatos.matriz.map((fila) => {
          if (
            fila.tipoPlato.id === tipoPlato.id &&
            fila.tipoPlato.fila_orden === tipoPlato.fila_orden &&
            (fila.tipoPlato.rol || 'unico') === rolEfectivo
          ) {
            return {
              ...fila,
              dias: fila.dias.map((dia) =>
                dia.fecha === fecha ? { ...dia, celdaOmitida: omitida } : dia
              ),
            };
          }
          return fila;
        });
        return { ...prevDatos, matriz: nuevaMatriz };
      });
    } catch (error: any) {
      console.error('Error toggle celda omitida:', error);
      const msg =
        error?.response?.data?.error ||
        'No se pudo marcar la celda como "no aplica"';
      setToastMensaje(msg);
      setToastTipo('error');
      setMostrarToast(true);
      setTimeout(() => setMostrarToast(false), 3000);
    }
  };

  const agregarTipoPlato = async (tipoPlato: TipoPlato) => {
    if (!datos || !id) return;

    try {
      console.log(`➕ Agregando fila para tipo: ${tipoPlato.nombre}`);

      // Marcar que hay cambios pendientes
      setCambiosPendientes(true);

      // Guardar la acción para procesarla al guardar
      const nombreBase = tipoPlato.nombre.replace(/\s+\d+$/, '');
      const clave = `agregar-fila-${nombreBase}-${Date.now()}`;
      setCambiosLocales((prev) => {
        const nuevos = new Map(prev);
        nuevos.set(clave, {
          recetaId: -1, // Usamos -1 para indicar que es una acción de agregar fila
          tipoPlato: { ...tipoPlato, nombre: nombreBase },
        });
        return nuevos;
      });

      // Actualización optimista: insertar la(s) nueva(s) fila(s) en la matriz.
      // Tipos simples → 1 fila con rol='unico'.
      // Tipos combinados → 2 filas (rol='fondo' + rol='guarnicion') para
      // representar la misma opción.
      setDatos((prevDatos) => {
        if (!prevDatos) return prevDatos;

        // Próximo número de opción para este tipo (mismo nombre base).
        const filasMismoTipo = prevDatos.matriz.filter(
          (f) => f.tipoPlato.nombre.replace(/\s+\d+$/, '') === nombreBase
        );
        const nuevoFilaOrden =
          Math.max(
            0,
            ...filasMismoTipo.map((f) => f.tipoPlato.fila_orden || 1)
          ) + 1;

        const buildFila = (rol: RolMinutaDetalle): FilaTipoPlato => ({
          tipoPlato: {
            ...tipoPlato,
            nombre: `${nombreBase} ${nuevoFilaOrden}`,
            fila_orden: nuevoFilaOrden,
            rol,
          },
          dias: prevDatos.dias.map((d) => ({
            fecha: d.fecha,
            nombre: d.nombre,
            receta: null,
            minutaDetalladaId: null,
          })),
        });

        const nuevasFilas: FilaTipoPlato[] = tipoPlatoUsaFondoGuarnicion(
          tipoPlato.id
        )
          ? [buildFila('fondo'), buildFila('guarnicion')]
          : [buildFila('unico')];

        // Insertar después de la última fila del mismo tipo para mantener agrupación.
        let indiceInsercion = prevDatos.matriz.length;
        for (let i = prevDatos.matriz.length - 1; i >= 0; i--) {
          const n = prevDatos.matriz[i].tipoPlato.nombre.replace(/\s+\d+$/, '');
          if (n === nombreBase) {
            indiceInsercion = i + 1;
            break;
          }
        }
        const nuevaMatriz = [
          ...prevDatos.matriz.slice(0, indiceInsercion),
          ...nuevasFilas,
          ...prevDatos.matriz.slice(indiceInsercion),
        ];

        return { ...prevDatos, matriz: nuevaMatriz };
      });

      console.log('✅ Acción de agregar fila marcada para guardar');
    } catch (error) {
      console.error('❌ Error agregando tipo de plato:', error);
    }
  };

  const eliminarFilaTipoPlato = async (tipoPlato: TipoPlato) => {
    if (!datos || !id) return;

    try {
      console.log(`🗑️ Eliminando fila para tipo: ${tipoPlato.nombre}`);

      // Filas afectadas por la eliminación: para tipos combinados son 2
      // (fondo+guarnicion) en la misma opción; para tipos simples es 1.
      const filasAfectadas = datos.matriz.filter(
        (fila) =>
          fila.tipoPlato.id === tipoPlato.id &&
          fila.tipoPlato.fila_orden === tipoPlato.fila_orden
      );

      if (filasAfectadas.length > 0) {
        const recetasAsignadas = filasAfectadas.flatMap((f) =>
          f.dias.filter((dia) => dia.receta !== null)
        );

        if (recetasAsignadas.length > 0) {
          // Mostrar modal de confirmación
          setModalConfirmConfig({
            title: 'Eliminar Fila con Recetas',
            message: `Esta fila tiene ${recetasAsignadas.length} recetas asignadas.\n\n¿Estás seguro de eliminarla? Se perderán todas las recetas de esta fila.`,
            type: 'danger',
            onConfirm: async () => {
              await ejecutarEliminacionFila(tipoPlato);
            },
          });
          setMostrarModalConfirm(true);
          return;
        }
      }

      // Si no hay recetas, eliminar directamente
      await ejecutarEliminacionFila(tipoPlato);
    } catch (error: any) {
      console.error('❌ Error eliminando fila:', error);

      // Manejar errores específicos del backend
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      }
    }
  };

  const ejecutarEliminacionFila = async (tipoPlato: TipoPlato) => {
    if (!datos || !id) return;

    try {
      console.log('✅ Marcando fila para eliminación');

      // Marcar que hay cambios pendientes
      setCambiosPendientes(true);

      // Guardar la acción para procesarla al guardar
      const nombreBase = tipoPlato.nombre.replace(/\s+\d+$/, '');
      const clave = `eliminar-fila-${nombreBase}-${Date.now()}`;
      setCambiosLocales((prev) => {
        const nuevos = new Map(prev);
        nuevos.set(clave, {
          recetaId: -2, // Usamos -2 para indicar que es una acción de eliminar fila
          tipoPlato: { ...tipoPlato, nombre: nombreBase },
        });
        return nuevos;
      });

      // Actualización optimista: remover TODAS las filas (ambos rol si es
      // combinado) que comparten esta opción.
      setDatos((prevDatos) => {
        if (!prevDatos) return prevDatos;
        const nuevaMatriz = prevDatos.matriz.filter(
          (f) =>
            !(
              f.tipoPlato.id === tipoPlato.id &&
              (f.tipoPlato.fila_orden || 1) === (tipoPlato.fila_orden || 1)
            )
        );
        return { ...prevDatos, matriz: nuevaMatriz };
      });
    } catch (error: any) {
      console.error('❌ Error eliminando fila:', error);

      // Manejar errores específicos del backend
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      }
    }
  };

  const toggleTipoOmitido = async (tipoPlato: TipoPlato) => {
    if (!id || !datos) return;

    // Bloquear solo en estados terminales.
    if (
      datos.minuta.estado &&
      ESTADOS_BLOQUEAN_EDICION.includes(datos.minuta.estado)
    ) {
      setModalConfirmConfig({
        title: 'Acción No Permitida',
        message: `No se puede modificar una minuta en estado "${datos.minuta.estado}".`,
        type: 'warning',
        confirmText: 'Entendido',
        cancelText: '',
        onConfirm: () => {
          setMostrarModalConfirm(false);
        },
      });
      setMostrarModalConfirm(true);
      return;
    }

    // Extraer nombre base sin número de fila
    const nombreBase = tipoPlato.nombre.replace(/\s+\d+$/, '');
    const omitir = !tipoPlato.omitido;

    console.log(
      `${omitir ? '🚫' : '✅'} ${omitir ? 'Omitiendo' : 'Reactivando'} tipo: ${nombreBase}`
    );

    // Si va a omitir y hay recetas, mostrar confirmación
    if (omitir) {
      const filasDelTipo = datos.matriz.filter(
        (fila) => fila.tipoPlato.nombre.replace(/\s+\d+$/, '') === nombreBase
      );

      const recetasAsignadas = filasDelTipo.reduce((total, fila) => {
        return total + fila.dias.filter((dia) => dia.receta !== null).length;
      }, 0);

      if (recetasAsignadas > 0) {
        setModalConfirmConfig({
          title: 'Omitir Tipo de Plato',
          message: `Este tipo de plato tiene ${recetasAsignadas} receta${recetasAsignadas > 1 ? 's' : ''} asignada${recetasAsignadas > 1 ? 's' : ''}.\n\nAl omitirlo, se eliminarán automáticamente todas estas recetas.\n\n¿Deseas continuar?`,
          type: 'danger',
          confirmText: 'Sí, Omitir',
          cancelText: 'Cancelar',
          onConfirm: async () => {
            setMostrarModalConfirm(false);
            await ejecutarToggleTipo(nombreBase, omitir);
          },
        });
        setMostrarModalConfirm(true);
        return;
      }
    }

    // Si no hay recetas o se está reactivando, ejecutar directamente
    await ejecutarToggleTipo(nombreBase, omitir);
  };

  const toggleTodosTiposPlato = async () => {
    if (!id || !datos) return;

    // Determinar si hay algún tipo activo (no omitido)
    const hayTiposActivos =
      datos.tiposPlato?.some((tp) => !tp.omitido) ?? false;

    // Si hay tipos activos, omitir todos; si no, activar todos
    const omitirTodos = hayTiposActivos;

    console.log(
      `${omitirTodos ? '🚫' : '✅'} ${omitirTodos ? 'Omitiendo' : 'Activando'} todos los tipos de plato`
    );

    // Marcar que hay cambios pendientes
    setCambiosPendientes(true);

    // Procesar cada tipo de plato
    if (datos.tiposPlato) {
      for (const tipo of datos.tiposPlato) {
        const nombreBase = tipo.nombre;
        const clave = `toggle-tipo-${nombreBase}-${Date.now()}-${Math.random()}`;

        setCambiosLocales((prev) => {
          const nuevos = new Map(prev);
          nuevos.set(clave, {
            recetaId: omitirTodos ? -3 : -4,
            tipoPlato: { id: 0, nombre: nombreBase, color: '', orden: 0 },
          });
          return nuevos;
        });
      }
    }

    // Actualizar UI inmediatamente
    setDatos((prevDatos) => {
      if (!prevDatos) return prevDatos;

      const nuevaMatriz = prevDatos.matriz.map((fila) => ({
        ...fila,
        tipoPlato: {
          ...fila.tipoPlato,
          omitido: omitirTodos,
        },
      }));

      const nuevosTiposPlato = prevDatos.tiposPlato?.map((tipo) => ({
        ...tipo,
        omitido: omitirTodos,
      }));

      return {
        ...prevDatos,
        matriz: nuevaMatriz,
        tiposPlato: nuevosTiposPlato,
      };
    });
  };

  const ejecutarToggleTipo = async (nombreBase: string, omitir: boolean) => {
    if (!id) return;

    try {
      console.log(
        `${omitir ? '🚫' : '✅'} Marcando toggle para: ${nombreBase}`
      );

      // Marcar que hay cambios pendientes
      setCambiosPendientes(true);

      // Guardar la acción para procesarla al guardar
      const clave = `toggle-tipo-${nombreBase}-${Date.now()}`;
      setCambiosLocales((prev) => {
        const nuevos = new Map(prev);
        nuevos.set(clave, {
          recetaId: omitir ? -3 : -4, // -3 para omitir, -4 para reactivar
          tipoPlato: { id: 0, nombre: nombreBase, color: '', orden: 0 },
        });
        return nuevos;
      });

      // Actualizar UI inmediatamente
      setDatos((prevDatos) => {
        if (!prevDatos) return prevDatos;

        // Actualizar matriz
        const nuevaMatriz = prevDatos.matriz.map((fila) => {
          const filaNombreBase = fila.tipoPlato.nombre.replace(/\s+\d+$/, '');
          if (filaNombreBase === nombreBase) {
            return {
              ...fila,
              tipoPlato: {
                ...fila.tipoPlato,
                omitido: omitir,
              },
            };
          }
          return fila;
        });

        // Actualizar tiposPlato también
        const nuevosTiposPlato = prevDatos.tiposPlato?.map((tipo) => {
          if (tipo.nombre === nombreBase) {
            return {
              ...tipo,
              omitido: omitir,
            };
          }
          return tipo;
        });

        return {
          ...prevDatos,
          matriz: nuevaMatriz,
          tiposPlato: nuevosTiposPlato,
        };
      });
    } catch (error: any) {
      console.error('❌ Error en toggle tipo omitido:', error);

      const mensajeError =
        error.response?.data?.error ||
        'Error al cambiar el estado del tipo de plato';

      setModalConfirmConfig({
        title: 'Error',
        message: mensajeError,
        type: 'danger',
        confirmText: 'Entendido',
        cancelText: '',
        onConfirm: () => {
          setMostrarModalConfirm(false);
        },
      });
      setMostrarModalConfirm(true);
    }
  };

  // ============================================================================
  // FUNCIONES DEL SELECTOR INLINE
  // ============================================================================

  const activarSelector = (
    fecha: string,
    tipoId: number,
    tipoNombre: string,
    filaOrden: number,
    rol: RolMinutaDetalle
  ) => {
    const celdaId = `${fecha}-${tipoId}-${filaOrden}-${rol}`;
    setSelectorActivo(celdaId);
    setBusquedaReceta('');

    // Extraer nombre base sin número (ej: "Colación Principal 2" -> "Colación Principal")
    // trim defensivo por si el nombre del tipo en BD tiene espacios sobrantes
    const nombreBase = tipoNombre.replace(/\s+\d+$/, '').trim();
    // El useEffect que escucha tipoSelectorActivo dispara la carga inicial.
    setTipoSelectorActivo({ id: tipoId, nombre: nombreBase, rol });
  };

  const desactivarSelector = () => {
    setSelectorActivo('');
    setBusquedaReceta('');
    setRecetasDisponibles([]);
    setTipoSelectorActivo(null);
  };

  // Re-consulta el backend cuando cambia el texto de búsqueda o el tipo
  // activo. El filtrado server-side garantiza que la búsqueda alcance a
  // recetas que quedan más allá del `limit` del listado.
  useEffect(() => {
    if (!tipoSelectorActivo) return;
    // Sin debounce al abrir el selector (search vacío); con debounce al tipear.
    const delay = busquedaReceta.trim() ? 250 : 0;
    const timer = setTimeout(() => {
      cargarRecetasDisponibles(
        tipoSelectorActivo.id,
        tipoSelectorActivo.nombre,
        tipoSelectorActivo.rol,
        busquedaReceta
      );
    }, delay);
    return () => clearTimeout(timer);
  }, [busquedaReceta, tipoSelectorActivo]);

  const filtrarRecetas = () => {
    // El filtrado por nombre lo hace el backend; este método queda como
    // pass-through defensivo.
    if (!Array.isArray(recetasDisponibles)) {
      console.error('recetasDisponibles no es un array:', recetasDisponibles);
      return [];
    }
    return recetasDisponibles;
  };

  // ============================================================================
  // FUNCIONES DE CÁLCULO
  // ============================================================================

  const calcularCostoPorDia = (fecha: string): number => {
    if (!datos) return 0;

    let total = 0;
    datos.matriz.forEach((fila) => {
      const dia = fila.dias.find((d) => d.fecha === fecha);
      if (dia?.receta?.costo_total) {
        total += parseFloat(dia.receta.costo_total);
      }
    });

    return total;
  };

  const mostrarCostoTotal = (
    costoTotal: string | number | null | undefined
  ): string => {
    if (!costoTotal) return '$0';
    const costo =
      typeof costoTotal === 'string' ? parseFloat(costoTotal) : costoTotal;
    if (isNaN(costo)) return '$0';
    return `$${costo.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatearMonedaCLP = (monto: number): string => {
    // Solo devolver el valor numérico sin símbolo $ para evitar duplicación
    return `$${monto.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // ============================================================================
  // FUNCIONES AUXILIARES
  // ============================================================================

  const generarNombreDinamico = (
    fechaInicio: string,
    fechaFin: string
  ): string => {
    if (!fechaInicio || !fechaFin) return '';

    try {
      const inicio = new Date(fechaInicio + 'T00:00:00');
      const fin = new Date(fechaFin + 'T00:00:00');

      const formatearFecha = (fecha: Date) => {
        const dia = fecha.getDate().toString().padStart(2, '0');
        const meses = [
          'Ene',
          'Feb',
          'Mar',
          'Abr',
          'May',
          'Jun',
          'Jul',
          'Ago',
          'Sep',
          'Oct',
          'Nov',
          'Dic',
        ];
        const mes = meses[fecha.getMonth()];
        return `${dia} ${mes}`;
      };

      const fechaInicioStr = formatearFecha(inicio);
      const fechaFinStr = formatearFecha(fin);
      const año = fin.getFullYear();

      return `Minuta Semana ${fechaInicioStr} - ${fechaFinStr} ${año}`;
    } catch (error) {
      console.error('Error formateando fechas para nombre:', error);
      return 'Minuta Personalizada';
    }
  };

  const obtenerNombreActual = (): string => {
    // Si estamos editando fechas, usar el nombre dinámico
    if (editandoFechas && fechaInicio && fechaFin) {
      return generarNombreDinamico(fechaInicio, fechaFin);
    }

    // Si no estamos editando, usar el nombre original
    if (datos?.minuta?.nombre) {
      return datos.minuta.nombre;
    }

    // Fallback
    return 'Minuta Sin Nombre';
  };

  // ============================================================================
  // EFECTOS
  // ============================================================================

  useEffect(() => {
    // Actualizar nombre dinámico cuando cambien las fechas durante edición
    if (editandoFechas && fechaInicio && fechaFin) {
      const nuevoNombre = generarNombreDinamico(fechaInicio, fechaFin);
      _setNombreDinamico(nuevoNombre);
    }
  }, [editandoFechas, fechaInicio, fechaFin]);

  // ============================================================================
  // EFECTOS
  // ============================================================================

  useEffect(() => {
    cargarDatosMinuta();
  }, [id]);

  // ============================================================================
  // RENDERS DE ESTADO
  // ============================================================================

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-900 px-4 lg:px-8 xl:px-16 py-6 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 p-8 flex flex-col items-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-400 mb-4" />
          <p className="text-lg text-gray-100">Cargando editor de minuta...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 px-4 lg:px-8 xl:px-16 py-6 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 p-8 max-w-md">
          <AlertCircle className="mx-auto h-8 w-8 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-100 text-center mb-2">
            Error
          </h3>
          <p className="text-gray-300 text-center mb-4">{error}</p>
          <div className="flex space-x-3 justify-center">
            <button
              onClick={cargarDatosMinuta}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Reintentar
            </button>
            <button
              onClick={() => navigate('/minutas-nuevas')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!datos) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900 px-4 lg:px-8 xl:px-16 py-6">
      <div className="max-w-full mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-100 flex items-center">
                <ChefHat className="mr-2 text-blue-400" />
                Editor de Minuta
              </h1>
              {editandoTitulo ? (
                <div className="mt-2 flex items-center space-x-2">
                  <input
                    type="text"
                    value={nuevoTitulo}
                    onChange={(e) => setNuevoTitulo(e.target.value)}
                    className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-lg flex-1"
                    placeholder="Nuevo nombre de la minuta"
                    autoFocus
                  />
                  <button
                    onClick={actualizarTitulo}
                    className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm flex items-center"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Guardar
                  </button>
                  <button
                    onClick={() => {
                      setEditandoTitulo(false);
                      setNuevoTitulo('');
                    }}
                    className="bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-xl font-semibold text-gray-300">
                    {obtenerNombreActual()}
                  </p>
                  {cambiosPendientes && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-500 dark:bg-orange-600 text-white">
                      Cambios sin guardar
                    </span>
                  )}
                  {datos?.minuta?.estado === 'borrador' &&
                    (() => {
                      const faltan = calcularCeldasFaltantes().length;
                      return faltan > 0 ? (
                        <span
                          className="px-2 py-1 text-xs font-medium rounded-full bg-gray-600/60 text-gray-200 border border-gray-500/40"
                          title="Para publicar, todas las celdas obligatorias deben tener receta o estar marcadas como 'No aplica'. Guardar parcial está permitido."
                        >
                          Borrador — faltan {faltan} celdas para publicar
                        </span>
                      ) : null;
                    })()}
                  <button
                    onClick={() => {
                      setNuevoTitulo(datos?.minuta?.nombre || '');
                      setEditandoTitulo(true);
                    }}
                    className="text-gray-400 hover:text-white"
                    title="Editar nombre de la minuta"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => guardarCambios(false)}
                disabled={!cambiosPendientes || guardando}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center border border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                onClick={manejarCerrarEditor}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center border border-gray-600"
              >
                <X className="w-4 h-4 mr-2" />
                Cerrar Editor
              </button>
            </div>
          </div>

          {/* Información de la minuta */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-blue-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-300">
                    Total Días
                  </p>
                  <p className="text-2xl font-bold text-blue-400">
                    {datos.configuracion.totalDias}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
              <div className="flex items-center">
                <ChefHat className="h-8 w-8 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-300">
                    Tipos de Plato
                  </p>
                  <p className="text-2xl font-bold text-green-400">
                    {datos.configuracion.totalTiposPlato}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  {estadisticasEnVivo.totalCeldas}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-300">
                    Total Celdas
                  </p>
                  <p className="text-2xl font-bold text-purple-400 tabular-nums">
                    {estadisticasEnVivo.totalCeldas}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">
                  ✓
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-300">
                    Recetas Asignadas
                  </p>
                  <p className="text-2xl font-bold text-orange-400 tabular-nums">
                    {estadisticasEnVivo.recetasAsignadas}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Barra de completitud — 2026-06-16: % en vivo derivado de
              datos.matriz, sin esperar al backend. % y barra X/Y usan los
              mismos números (definición sesión #026). */}
          <div className="mt-4 bg-gray-700/60 p-4 rounded-lg border border-gray-600">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-300">
                  Completitud
                </span>
                <span className="text-2xl font-bold text-[#29ABE2] tabular-nums">
                  {estadisticasEnVivo.completitudPorcentaje}%
                </span>
              </div>
              <span className="text-xs text-gray-400 tabular-nums">
                {estadisticasEnVivo.celdasCompletas}/{estadisticasEnVivo.totalCeldas} casillas
                {estadisticasEnVivo.celdasOmitidas > 0 && (
                  <span className="ml-1 text-amber-400">
                    ({estadisticasEnVivo.celdasOmitidas} "No aplica")
                  </span>
                )}
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div
                className="bg-[#29ABE2] h-2 rounded-full transition-all duration-300"
                style={{ width: `${estadisticasEnVivo.completitudPorcentaje}%` }}
              />
            </div>
          </div>
        </div>

        {/* Editor de Fechas */}
        <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 p-6">
          <div className="mb-6">
            <div className="w-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-100">
                  Rango de Fechas
                </h2>
                <div className="flex items-center space-x-2">
                  {!editandoFechas ? (
                    <button
                      onClick={() => setEditandoFechas(true)}
                      className="flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Editar
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={actualizarFechas}
                        className="flex items-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        <Save className="w-4 h-4 mr-1" />
                        Guardar
                      </button>
                      <button
                        onClick={() => setEditandoFechas(false)}
                        className="flex items-center px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {editandoFechas ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Fecha Inicio
                    </label>
                    <input
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Fecha Fin
                    </label>
                    <input
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-gray-100"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900 rounded-lg p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-gray-300">
                    <div>
                      <span className="text-sm text-gray-400">Desde:</span>
                      <div className="font-medium">{fechaInicio}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">Hasta:</span>
                      <div className="font-medium">{fechaFin}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Tabla de Minuta — rediseño "hoja editorial" (2026-06-16).
              Tema CLARO local (solo dentro del wrapper, el resto de la
              app sigue oscuro). Acentos NexGen: cyan #29ABE2 (interactivo
              / focus) y naranja #F7941D (total del día / compuesto).
              Estructura del DOM, handlers, props y flujo NO cambian. */}
          <div className="rounded-2xl shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)] border border-stone-200 overflow-hidden bg-[#fdfcf8]">
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-4 py-4 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-[0.14em] sticky left-0 bg-stone-50 z-20 w-16 border-b border-stone-200">
                      <button
                        onClick={toggleTodosTiposPlato}
                        disabled={ESTADOS_BLOQUEAN_EDICION.includes(datos.minuta.estado || '')}
                        className="px-3 py-1.5 bg-white border border-stone-300 rounded-md hover:bg-stone-100 hover:border-[#29ABE2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mx-auto flex items-center justify-center gap-1 text-xs font-medium shadow-sm"
                        title={
                          datos.tiposPlato?.some((tp) => !tp.omitido)
                            ? 'Omitir todos los tipos de plato'
                            : 'Activar todos los tipos de plato'
                        }
                      >
                        {datos.tiposPlato?.some((tp) => !tp.omitido) ? (
                          <span className="text-emerald-600 font-bold">✓</span>
                        ) : (
                          <span className="text-stone-400 font-bold">✗</span>
                        )}
                        <span className="text-slate-700">Todo</span>
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-[0.14em] sticky left-16 bg-stone-50 z-20 border-b border-stone-200">
                      Tipo de plato
                    </th>
                    {datos.dias.map((dia) => (
                      <th
                        key={dia.fecha}
                        className="px-5 py-4 text-center min-w-[220px] border-b border-stone-200 bg-stone-50"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <div className="font-serif text-[15px] font-semibold uppercase tracking-[0.16em] text-slate-800">
                            {dia.nombre}
                          </div>
                          <div className="text-[11px] text-slate-500 tabular-nums">
                            {dia.fecha}
                          </div>
                          <div className="mt-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F7941D]/12 text-[#9b5b0e] border border-[#F7941D]/30">
                            <span className="text-[10px] uppercase tracking-wider font-semibold">Total</span>
                            <span className="font-bold text-xs tabular-nums">
                              {formatearMonedaCLP(
                                calcularCostoPorDia(dia.fecha)
                              )}
                            </span>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-[#fdfcf8]">
                  {(datos.tiposPlato && datos.tiposPlato.length > 0
                    ? datos.tiposPlato
                    : // Fallback: extraer tipos únicos de la matriz si no hay tiposPlato
                      datos.matriz.reduce((tipos: TipoPlato[], fila) => {
                        const nombreBase = fila.tipoPlato.nombre.replace(
                          /\s+\d+$/,
                          ''
                        );
                        if (!tipos.find((t) => t.nombre === nombreBase)) {
                          tipos.push({
                            id: fila.tipoPlato.id,
                            nombre: nombreBase,
                            color: fila.tipoPlato.color,
                            orden: fila.tipoPlato.orden,
                            omitido: false,
                          });
                        }
                        return tipos;
                      }, [])
                  ).map((tipo) => {
                    // Encontrar todas las filas de este tipo en la matriz
                    const filasDelTipo = datos.matriz.filter((fila) => {
                      const nombreBase = fila.tipoPlato.nombre.replace(
                        /\s+\d+$/,
                        ''
                      );
                      return nombreBase === tipo.nombre;
                    });

                    // 2026-06-16 — UI: la fila/sección de "Bebidas"
                    // (tipo_plato BEBIDA, id de dominio 11) se renderiza
                    // colapsada por default detrás de una barra compacta
                    // "Bebidas (N incluidas)". Sin tocar la lógica de
                    // auto-seed, guardado ni validación bebidas≤comida.
                    // Si NO está expandido, sólo dibujamos la barra y
                    // omitimos las <tr> internas (el render normal).
                    const tipoNombreNorm = tipo.nombre
                      .normalize('NFD')
                      .replace(/[̀-ͯ]/g, '')
                      .toUpperCase()
                      .trim();
                    const esBebida = tipoNombreNorm === 'BEBIDA';
                    const colSpanBarra = 2 + datos.dias.length;
                    const barraBebidas = esBebida ? (
                      <tr
                        key={`bebida-toggle-${tipo.nombre}`}
                        className="border-b border-stone-200"
                      >
                        <td
                          colSpan={colSpanBarra}
                          className="px-0 py-0 sticky left-0 z-10 bg-sky-50/70"
                        >
                          <button
                            type="button"
                            onClick={() => setBebidasExpandidas((v) => !v)}
                            aria-expanded={bebidasExpandidas}
                            aria-controls={`bebidas-panel-${tipo.nombre}`}
                            title={
                              bebidasExpandidas
                                ? 'Colapsar bebidas'
                                : 'Expandir bebidas'
                            }
                            className="w-full flex items-center gap-3 px-6 py-3 text-left text-sm font-medium text-slate-700 hover:bg-sky-100/60 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
                          >
                            {bebidasExpandidas ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                            <GlassWater className="w-4 h-4 text-[#29ABE2]" />
                            <span className="font-semibold uppercase tracking-[0.14em]">
                              Bebidas
                            </span>
                            <span className="text-xs text-slate-500">
                              ({filasDelTipo.length} incluida
                              {filasDelTipo.length !== 1 ? 's' : ''})
                            </span>
                          </button>
                        </td>
                      </tr>
                    ) : null;

                    if (esBebida && !bebidasExpandidas) {
                      // Solo la barra; ocultamos las filas internas.
                      return barraBebidas;
                    }

                    return [
                      barraBebidas,
                      ...filasDelTipo.map((fila, index) => (
                      <tr
                        key={`${fila.tipoPlato.id}-${fila.tipoPlato.fila_orden || 1}-${fila.tipoPlato.rol || 'unico'}`}
                        className={`group/row transition-colors hover:bg-stone-50 ${
                          index % 2 === 1 ? 'bg-stone-50/40' : ''
                        } ${tipo.omitido ? 'opacity-60' : ''} ${
                          fila.tipoPlato.rol === 'fondo'
                            ? 'compuesto-fondo'
                            : fila.tipoPlato.rol === 'guarnicion'
                              ? 'compuesto-guarnicion'
                              : ''
                        }`}
                      >
                        {/* Columna de checkbox */}
                        <td className="px-4 py-4 whitespace-nowrap sticky left-0 bg-inherit z-10 border-b border-stone-200 text-center">
                          {/* Checkbox solo en la primera fila de cada tipo */}
                          {index === 0 ? (
                            <input
                              type="checkbox"
                              checked={!tipo.omitido}
                              onChange={() => toggleTipoOmitido(tipo)}
                              disabled={ESTADOS_BLOQUEAN_EDICION.includes(datos.minuta.estado || '')}
                              className="w-4 h-4 text-emerald-600 bg-white border-stone-300 rounded focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                              title={
                                tipo.omitido
                                  ? 'Click para reactivar tipo de plato'
                                  : 'Click para omitir tipo de plato'
                              }
                            />
                          ) : (
                            <div className="w-4 h-4"></div>
                          )}
                        </td>
                        {/* Columna de tipo de plato */}
                        <td className="px-6 py-4 whitespace-nowrap sticky left-16 bg-inherit z-10 border-b border-stone-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-2.5 h-2.5 rounded-full ring-2 ring-white shadow-sm"
                                style={{
                                  backgroundColor: fila.tipoPlato.color,
                                }}
                              ></div>
                              <div className="flex flex-col">
                                <div
                                  className={`font-serif text-sm font-semibold uppercase tracking-[0.06em] ${tipo.omitido ? 'text-stone-400 line-through' : 'text-slate-800'}`}
                                >
                                  {fila.tipoPlato.nombre}
                                </div>
                                {/* Sub-etiqueta Fondo/Guarnición + badge "Plato
                                    compuesto" (sólo en la sub-fila 'fondo').
                                    Los dos componentes siguen siendo dos filas
                                    editables; el badge es puramente visual. */}
                                {tipoPlatoUsaFondoGuarnicion(
                                  fila.tipoPlato.id
                                ) &&
                                  fila.tipoPlato.rol &&
                                  fila.tipoPlato.rol !== 'unico' && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <span
                                        className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${
                                          tipo.omitido
                                            ? 'text-stone-400'
                                            : fila.tipoPlato.rol === 'fondo'
                                              ? 'text-amber-700'
                                              : 'text-emerald-700'
                                        }`}
                                      >
                                        {fila.tipoPlato.rol === 'fondo'
                                          ? 'Fondo'
                                          : 'Guarnición'}
                                      </span>
                                      {fila.tipoPlato.rol === 'fondo' && (
                                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F7941D]/15 text-[#9b5b0e] border border-[#F7941D]/30">
                                          Plato compuesto
                                        </span>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-2 opacity-0 group-hover/row:opacity-100 md:opacity-0 md:group-hover/row:opacity-100 transition-opacity sm:opacity-100 max-md:opacity-100">
                              {/* Botón eliminar opción: solo para filas con
                                  fila_orden > 1, y para tipos combinados solo
                                  en la sub-fila 'fondo' (la opción se elimina
                                  completa con sus 2 sub-filas). */}
                              {fila.tipoPlato.fila_orden &&
                                fila.tipoPlato.fila_orden > 1 &&
                                (fila.tipoPlato.rol || 'unico') !==
                                  'guarnicion' && (
                                  <button
                                    onClick={() =>
                                      eliminarFilaTipoPlato(fila.tipoPlato)
                                    }
                                    className="flex items-center justify-center w-6 h-6 rounded-md bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 transition-colors"
                                    title={`Quitar opción ${fila.tipoPlato.fila_orden}`}
                                    disabled={ESTADOS_BLOQUEAN_EDICION.includes(
                                      datos.minuta.estado || ''
                                    )}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              {/* Botón agregar fila adicional, solo en la última fila del tipo */}
                              {index === filasDelTipo.length - 1 && (
                                <button
                                  onClick={() =>
                                    agregarTipoPlato(fila.tipoPlato)
                                  }
                                  className="flex items-center justify-center w-6 h-6 rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200 hover:text-emerald-800 transition-colors"
                                  title={`Agregar otra fila de ${fila.tipoPlato.nombre.replace(/\s+\d+$/, '')}`}
                                  disabled={ESTADOS_BLOQUEAN_EDICION.includes(datos.minuta.estado || '')}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        {fila.dias.map((celda) => {
                          const celdaId = `${celda.fecha}-${fila.tipoPlato.id}-${fila.tipoPlato.fila_orden || 1}-${fila.tipoPlato.rol || 'unico'}`;
                          const esSelectorActivo = selectorActivo === celdaId;

                          return (
                            <td
                              key={celda.fecha}
                              className="px-3 py-3 text-sm align-top border-b border-stone-200"
                            >
                              {celda.receta ? (
                                // Celda con receta asignada — card editorial
                                // blanco con border-l del color del tipo de plato.
                                <div
                                  className="group/cell relative bg-white p-3 rounded-lg border border-stone-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_-4px_rgba(15,23,42,0.12)] hover:border-stone-300 transition-all border-l-[3px]"
                                  style={{ borderLeftColor: fila.tipoPlato.color }}
                                >
                                  <div className="font-serif text-[13px] leading-snug font-semibold text-slate-800 pr-6">
                                    {celda.receta.nombre}
                                  </div>
                                  <div className="flex items-center justify-between mt-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 text-slate-600 text-[11px] font-medium tabular-nums">
                                      {mostrarCostoTotal(
                                        celda.receta.costo_total
                                      )}
                                    </span>
                                    <button
                                      onClick={() =>
                                        removerReceta(
                                          celda.fecha,
                                          fila.tipoPlato
                                        )
                                      }
                                      className="text-stone-400 hover:text-red-600 p-1 rounded opacity-0 group-hover/cell:opacity-100 md:opacity-0 md:group-hover/cell:opacity-100 max-md:opacity-100 transition-opacity"
                                      title="Remover receta"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              ) : celda.celdaOmitida ? (
                                // Celda marcada como "no aplica" — muted cream
                                // para indicar omisión deliberada.
                                <div className="bg-stone-100/60 border border-dashed border-stone-300 rounded-lg p-3 flex items-center justify-between text-stone-500">
                                  <span className="text-xs italic">
                                    No aplica
                                  </span>
                                  <button
                                    onClick={() =>
                                      toggleCeldaOmitida(
                                        celda.fecha,
                                        fila.tipoPlato,
                                        false
                                      )
                                    }
                                    className="text-stone-400 hover:text-[#29ABE2] p-1 rounded"
                                    title="Reactivar celda"
                                  >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                // Celda vacía con selector inline
                                <div className="relative">
                                  {esSelectorActivo ? (
                                    <div className="bg-white border-2 border-[#29ABE2] rounded-lg p-2 shadow-md">
                                      <input
                                        type="text"
                                        placeholder="Buscar receta..."
                                        value={busquedaReceta}
                                        onChange={(e) =>
                                          setBusquedaReceta(e.target.value)
                                        }
                                        onBlur={() => {
                                          setTimeout(desactivarSelector, 200);
                                        }}
                                        className="w-full px-2 py-1.5 text-xs bg-stone-50 text-slate-800 border border-stone-200 rounded focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40 focus:border-[#29ABE2]"
                                        autoFocus
                                      />
                                      {/* Dropdown de recetas */}
                                      {cargandoRecetas ? (
                                        <div className="absolute top-full left-0 right-0 bg-white border border-stone-200 rounded-b z-20 shadow-md">
                                          <div className="p-2 text-xs text-slate-500">
                                            Cargando...
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="absolute top-full left-0 right-0 bg-white border border-stone-200 rounded-b max-h-44 overflow-y-auto z-20 shadow-md">
                                          {filtrarRecetas().length > 0 ? (
                                            filtrarRecetas().map((receta) => (
                                              <div
                                                key={receta.id}
                                                onClick={() =>
                                                  asignarReceta(
                                                    celda.fecha,
                                                    fila.tipoPlato,
                                                    receta.id
                                                  )
                                                }
                                                className="p-2 hover:bg-sky-50 cursor-pointer border-b border-stone-100 last:border-b-0"
                                              >
                                                <div className="text-xs font-medium text-slate-800">
                                                  {receta.nombre}
                                                </div>
                                                <div className="text-[11px] text-slate-500 tabular-nums mt-0.5">
                                                  {formatearMonedaCLP(
                                                    parseFloat(
                                                      receta.costo_total
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="p-2 text-xs text-slate-500">
                                              {busquedaReceta.trim()
                                                ? 'No se encontraron recetas'
                                                : 'No hay recetas disponibles'}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="relative w-full min-h-[64px] bg-white hover:bg-sky-50/50 border border-dashed border-stone-300 hover:border-[#29ABE2] rounded-lg transition-colors">
                                      <button
                                        onClick={() =>
                                          activarSelector(
                                            celda.fecha,
                                            fila.tipoPlato.id,
                                            fila.tipoPlato.nombre,
                                            fila.tipoPlato.fila_orden || 1,
                                            fila.tipoPlato.rol || 'unico'
                                          )
                                        }
                                        className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-stone-400 hover:text-[#29ABE2]"
                                        title="Asignar receta"
                                      >
                                        <Plus className="w-5 h-5" strokeWidth={1.6} />
                                        <span className="text-[10px] uppercase tracking-wider font-medium">
                                          Agregar
                                        </span>
                                      </button>
                                      {/* Botón "No aplica" — siempre visible
                                          en móvil, hover-reveal en desktop. */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleCeldaOmitida(
                                            celda.fecha,
                                            fila.tipoPlato,
                                            true
                                          );
                                        }}
                                        className="absolute top-1 right-1 z-10 px-2 py-0.5 rounded-md text-[10px] font-medium bg-stone-100 text-stone-600 border border-stone-200 hover:bg-amber-100 hover:text-amber-800 hover:border-amber-300 transition-colors opacity-0 group-hover/row:opacity-100 max-md:opacity-100"
                                        title="Marcar como No aplica (no se ofrece este día)"
                                      >
                                        No aplica
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    )),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Toast de feedback */}
      {mostrarToast && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div
            className={`rounded-lg shadow-xl border px-6 py-4 flex items-center space-x-3 ${
              toastTipo === 'success'
                ? 'bg-green-600 border-green-500 text-white'
                : 'bg-red-600 border-red-500 text-white'
            }`}
          >
            {toastTipo === 'success' ? (
              <Save className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{toastMensaje}</span>
          </div>
        </div>
      )}

      {/* Modal de confirmación al cerrar */}
      {mostrarModalCerrar && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setMostrarModalCerrar(false)}
            ></div>

            <div className="inline-block align-bottom bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700">
              <div className="bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-100">
                      Cambios sin guardar
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-300">
                        Tienes cambios sin guardar. ¿Qué deseas hacer?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMostrarModalCerrar(false);
                    guardarYCerrar();
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:w-auto sm:text-sm"
                >
                  Guardar y salir
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMostrarModalCerrar(false);
                    cerrarSinGuardar();
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-gray-600 text-base font-medium text-white hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Salir sin guardar
                </button>
                <button
                  type="button"
                  onClick={() => setMostrarModalCerrar(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-gray-800 text-base font-medium text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={mostrarModalConfirm}
        onClose={() => setMostrarModalConfirm(false)}
        onConfirm={modalConfirmConfig.onConfirm}
        title={modalConfirmConfig.title}
        message={modalConfirmConfig.message}
        type={modalConfirmConfig.type}
        confirmText={modalConfirmConfig.confirmText || 'Confirmar'}
        cancelText={modalConfirmConfig.cancelText || 'Cancelar'}
      />
    </div>
  );
};

export default EditorMinuta;
